import { useState, useEffect } from "react"
import { CalendarIcon } from "lucide-react"
import { format, parse, isValid } from "date-fns"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { Calendar } from "../components/ui/calendar"
import { Popover, PopoverTrigger, PopoverContent } from "../components/ui/popover"
import { cn } from "../lib/utils"
import { stripPresetConflicts } from "../lib/preset-utils"

export function DatePickerRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent } = useRenderContext()
  // Theme-v2 atom defaults feed the fallback chain.
  const atomDefaults = useAtomDefaults("date-picker")
  const resolvedVariant = (element.variant as string | undefined)
    ?? atomDefaults.defaultVariant
    ?? "default"
  const resolvedSize = (element as { size?: string }).size
    ?? atomDefaults.defaultSize
    ?? "default"
  const [open, setOpen] = useState(false)
  const [localDate, setLocalDate] = useState<Date | undefined>(undefined)

  // Read bound value from state (e.g. bind: "failedTransaction.dateFrom")
  const boundValue = element.bind
    ? String(resolveSymbol(element.bind.startsWith("#{") ? element.bind : `#{${element.bind}}`) ?? "")
    : ""

  // Sync local state from bound state
  useEffect(() => {
    if (boundValue) {
      const parsed = parse(boundValue, "yyyy-MM-dd", new Date())
      if (isValid(parsed)) {
        setLocalDate(parsed)
      }
    } else {
      setLocalDate(undefined)
    }
  }, [boundValue])

  const placeholder = element.placeholder
    ? String(resolveSymbol(element.placeholder) ?? "")
    : "Select date"

  // Display format (visual only); event value always uses yyyy-MM-dd
  const displayFormat = element.displayFormat
    ? String(resolveSymbol(element.displayFormat) ?? "yyyy/MM/dd")
    : "yyyy/MM/dd"

  const handleSelect = (date: Date | undefined) => {
    setLocalDate(date)
    const value = date ? format(date, "yyyy-MM-dd") : ""
    dispatchEvent(element.id, "change", { value })
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        data-empty={!localDate}
        className={cn(
          "cn-button group/button inline-flex shrink-0 items-center whitespace-nowrap rounded-lg text-sm font-medium outline-none transition-all",
          "border border-input bg-background shadow-xs hover:bg-accent hover:text-accent-foreground",
          "h-9 px-4 py-2 has-[>svg]:px-3",
          "justify-start text-left font-normal data-[empty=true]:text-muted-foreground",
          stripPresetConflicts(element.className),
        )}
        data-testid={`date-picker-${element.id}`}
        data-atom-type="date-picker"
        data-variant={resolvedVariant}
        data-size={resolvedSize}
      >
        <CalendarIcon className="mr-2 h-4 w-4 opacity-50" />
        {localDate ? format(localDate, displayFormat) : placeholder}
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={localDate}
          onSelect={handleSelect}
        />
      </PopoverContent>
    </Popover>
  )
}
